/**
 * The paths the ink sprites can walk: the centre-lines of the page margins
 * and of every gutter between panels. Segments are axis-aligned:
 *   { axis: "v" | "h", at, start, end }
 * "v" runs vertically at x = at from y = start to y = end; "h" horizontally.
 */
import { findGutters } from "./panelGeometry";

export const buildGutterNetwork = (cells, gutter, pageWidth, pageHeight) => {
  const half = gutter / 2;
  const segments = [];
  if (gutter >= 4) {
    segments.push(
      { axis: "v", at: half, start: half, end: pageHeight - half },
      { axis: "v", at: pageWidth - half, start: half, end: pageHeight - half },
      { axis: "h", at: half, start: half, end: pageWidth - half },
      { axis: "h", at: pageHeight - half, start: half, end: pageWidth - half }
    );
  }
  findGutters(cells, gutter).forEach((run) => {
    // Reach half a gutter past the panels so it meets the lines it crosses
    segments.push({
      axis: run.axis === "x" ? "v" : "h",
      at: run.at + half,
      start: run.start - half,
      end: run.end + half,
    });
  });
  return segments;
};

const EPS = 1;

/** Where other segments cross segment i: [{ t, j }], t along segment i. */
export const crossingsOf = (segments, i) => {
  const seg = segments[i];
  const out = [];
  segments.forEach((other, j) => {
    if (j === i || other.axis === seg.axis) return;
    if (other.at < seg.start - EPS || other.at > seg.end + EPS) return;
    if (seg.at < other.start - EPS || seg.at > other.end + EPS) return;
    out.push({ t: other.at, j });
  });
  return out;
};

/** Page position of the point at t along a segment. */
export const pointOn = (seg, t) => (seg.axis === "v" ? { x: seg.at, y: t } : { x: t, y: seg.at });

/** A small deterministic random generator, so each sprite keeps its own fuzz. */
export const seededRandom = (seed) => {
  let s = seed % 2147483647;
  if (s <= 0) s += 2147483646;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
};

/** Soot-sprite fuzz: a jagged ring of hairs around a round body (SVG path). */
export const fuzzPath = (radius, seed) => {
  const rand = seededRandom(seed);
  const hairs = 26;
  const points = [];
  for (let i = 0; i < hairs * 2; i++) {
    const angle = (Math.PI * i) / hairs;
    const r = i % 2 === 0 ? radius * (1.12 + rand() * 0.22) : radius * (0.86 + rand() * 0.08);
    points.push([Math.cos(angle) * r, Math.sin(angle) * r]);
  }
  return `M ${points.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(" L ")} Z`;
};
